import React from "react";
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
  arrayMove,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";

function SortableRow({ id, children, disabled }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="group">
      <div className="flex items-start gap-2">
        <button
          type="button"
          className={
            "mt-1 rounded-md p-1 text-slate-200/60 hover:bg-white/10 hover:text-slate-100 " +
            (disabled ? "cursor-not-allowed opacity-40" : "cursor-grab")
          }
          aria-label="Drag to reorder"
          disabled={disabled}
          {...attributes}
          {...listeners}
          data-testid={`node-drag-handle-${id}`}
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <div className="min-w-0 flex-1">{children}</div>
      </div>
    </div>
  );
}

export default function NodeListReorder({
  items,
  getId,
  disabled,
  onReorder,
  children,
}) {
  const sensors = useSensors(
    // Use a pointer-down activation to improve reliability across browsers/test runners.
    useSensor(PointerSensor, { activationConstraint: { distance: 1 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const ids = items.map((it) => getId(it));

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={(event) => {
        const { active, over } = event;
        if (!over) return;
        if (active.id === over.id) return;

        const oldIndex = ids.indexOf(active.id);
        const newIndex = ids.indexOf(over.id);
        if (oldIndex < 0 || newIndex < 0) return;

        const next = arrayMove(items, oldIndex, newIndex);
        onReorder?.(next);
      }}
      onDragCancel={() => {
        // no-op; ensures we have a cancel handler for debugging if needed
      }}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div className="space-y-2">
          {items.map((it, idx) => (
            <SortableRow key={getId(it)} id={getId(it)} disabled={disabled}>
              {children(it, idx)}
            </SortableRow>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
